import React, { useState, useMemo } from "react"
import { DealCard } from "./DealCard"
import { STAGE_KIND_STYLES, formatMoney } from "../constants"

// Kanban board: one column per stage of the active pipeline. Cards drag between
// columns; dropping calls onMoveDeal(deal, stage). Won/Lost stages are valid
// drop targets — the backend ChangeStage handles the win/lose side-effects.
export function DealsKanban({ stages, deals, onMoveDeal }) {
  const [draggingId, setDraggingId] = useState(null)
  const [overStageId, setOverStageId] = useState(null)
  const dragged = React.useRef(null)

  const dealsByStage = useMemo(() => {
    const map = {}
    for (const s of stages) map[s.id] = []
    for (const d of deals) {
      if (map[d.stage_id]) map[d.stage_id].push(d)
    }
    return map
  }, [stages, deals])

  function handleDragStart(e, deal) {
    dragged.current = deal
    setDraggingId(deal.id)
    e.dataTransfer.effectAllowed = "move"
  }

  function handleDragEnd() {
    dragged.current = null
    setDraggingId(null)
    setOverStageId(null)
  }

  function handleDrop(stage) {
    const deal = dragged.current
    if (deal && deal.stage_id !== stage.id) onMoveDeal(deal, stage)
    handleDragEnd()
  }

  return (
    <div className="flex gap-3 overflow-x-auto pb-3">
      {stages.map((stage) => {
        const items = dealsByStage[stage.id] || []
        const kind = STAGE_KIND_STYLES[stage.kind] || STAGE_KIND_STYLES.open
        const total = items.reduce((sum, d) => sum + (Number(d.value) || 0), 0)
        const isOver = overStageId === stage.id
        const isClosed = stage.kind === "won" || stage.kind === "lost"

        return (
          <div
            key={stage.id}
            onDragOver={(e) => { e.preventDefault(); setOverStageId(stage.id) }}
            onDragLeave={() => setOverStageId((cur) => (cur === stage.id ? null : cur))}
            onDrop={() => handleDrop(stage)}
            className={[
              // Grow to fill the row but never collapse below a readable width.
              "flex min-w-[15rem] flex-1 flex-col rounded-2xl border px-4 py-3 transition-colors",
              isClosed ? "bg-slate-100/50" : "bg-slate-50/60",
              isOver ? "border-blue-300 bg-blue-50/60" : "border-slate-200",
            ].join(" ")}
          >
            {/* Column header */}
            <div className="mb-3 pt-2 flex flex-col items-center justify-center px-1">
              <div className="flex items-center justify-center gap-2 mb-1">
                <span className={`h-2 w-2 rounded-full ${kind.dot}`} />
                <span className={`text-[13px] font-semibold ${kind.header}`}>{stage.name}</span>
                <span className="rounded-full bg-slate-200/70 px-1.5 text-[11px] font-medium text-slate-600">
                  {items.length}
                </span>
                {isClosed && (
                  <span className="rounded bg-slate-200 px-1 text-[9px] font-semibold uppercase tracking-wide text-slate-500">
                    Closed
                  </span>
                )}
              </div>
              <div className="text-[11px] text-slate-400 text-center">{formatMoney(total)}</div>
            </div>

            {/* Cards */}
            <div className="flex min-h-[60px] flex-1 flex-col gap-2">
              {items.map((deal) => (
                <DealCard
                  key={deal.id}
                  deal={deal}
                  dragging={draggingId === deal.id}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                />
              ))}
              {items.length === 0 && (
                <div className="rounded-lg border border-dashed border-slate-200 py-6 text-center text-[12px] text-slate-300">
                  Drop here
                </div>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}
